import { useMemo, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { Pencil } from 'lucide-react';

import { useSetPersonPositions } from '../api/planning-hooks';
import { PROJECT_PRIORITY_LABELS } from '../api/types';
import { statusLabel, statusOptions } from '../lib/statuses';
import type { Person, Project, RegistryDocument } from '../api/types';
import { isOverdue, personCapacity } from '../lib/capacity';
import type { StatusReport } from '../lib/capacity';
import { orderedIds, sortPersonQueueManual } from '../lib/manual-order';
import { DEFAULT_QUEUE_SORT, QUEUE_SORT_KEYS, QUEUE_SORT_LABELS, sortPersonQueueWith } from '../lib/queue-order';
import type { QueueSortKey } from '../lib/queue-order';
import { formatDisplayDate } from '../lib/date';
import { matchesFilters } from '../lib/filters';
import { priorityRowClass } from '../lib/priority-class';
import { INTERACTIVE, manualRowDragProps, useManualOrder } from '../hooks/useManualOrder';
import { ProjectEditModal } from '../components/ProjectEditModal';
import { DragHandleCell } from '../components/DragHandleCell';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { FilterBar } from '../components/FilterBar';
import { PageHeader, FilteredEmptyState } from '../components/view-parts';
import { Badge, Button, EmptyState } from '../components/ui';
import { useFilters, useRegistryFilterContext } from '../state/FiltersProvider';

/**
 * F-18: «Исполнители» — the per-person report. A header card per person
 * (name, role, team, capacity = FULL project hours split by status, counts)
 * above the personal queue. The queue's default order is «Вручную» —
 * `personPosition`, arranged by dragging; the other sort modes keep the
 * `personPriority` ranking (queue №2). An inactive person keeps their card but
 * has nothing to drag.
 */
export function PeoplePage(): ReactElement {
  return <RegistryGate label="Загрузка реестра">{(registry) => <PeopleView registry={registry} />}</RegistryGate>;
}

function PeopleView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { filters, isActive } = useFilters();
  const { maps } = useRegistryFilterContext();

  const teamsById = useMemo(() => new Map(registry.teams.map((team) => [team.id, team.name])), [registry.teams]);

  /**
   * One filtered queue per person (the shared F-13 predicate drives it): the
   * team filter (F-19) keeps only people with ≥1 matching project — their card
   * would be an empty shell otherwise (owner feedback #2 + owner: «filter by
   * the team to all pages»).
   */
  const people = useMemo(() => {
    if (!maps) return [];
    const selectedPeople = filters.personId ?? [];
    const visible = selectedPeople.length > 0 ? registry.people.filter((person) => selectedPeople.includes(person.id)) : registry.people;
    const withQueue = visible.map((person) => ({
      person,
      queue: registry.projects.filter((project) => matchesFilters(project, filters, maps) && project.assignments.some((a) => a.personId === person.id)),
    }));
    const listed = selectedPeople.length > 0 || !isActive ? withQueue : withQueue.filter((entry) => entry.queue.length > 0);
    return listed
      .sort((a, b) => Number(b.person.active) - Number(a.person.active) || a.person.name.localeCompare(b.person.name, 'ru'))
      .map((entry) => entry.person);
  }, [registry.people, registry.projects, filters, maps, isActive]);

  // Edit by ID (see ProjectsPage): a healed cache after a 409 re-opens the
  // dialog on the fresh project, never on a captured stale row.
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const editing = registry.projects.find((project) => project.id === editingId);

  return (
    <>
      <PageHeader
        title="Исполнители"
        sub="Личная очередь (очередь №2): «Вручную» — порядок перетаскиванием, остальные режимы — по personPriority; загрузка в часах — полный объём проекта (F-03)."
      />

      <SummaryChips />

      <FilterBar />

      {people.map((person) => (
        <PersonCard
          key={person.id}
          person={person}
          registry={registry}
          teamsById={teamsById}
          maps={maps}
          filters={filters}
          onEdit={setEditingId}
        />
      ))}

      {/* Owner feedback #2: active filters that leave nobody hide the block. */}
      {people.length === 0 ? (
        isActive && (filters.personId?.length ?? 0) === 0 ? null : (
          <section className="card">
            <FilteredEmptyState what="исполнители" whatGenitive="исполнителей" />
          </section>
        )
      ) : null}

      {editing ? <ProjectEditModal open onClose={() => setEditingId(undefined)} registry={registry} project={editing} /> : null}
    </>
  );
}

function PersonCard({
  person,
  registry,
  teamsById,
  maps,
  filters,
  onEdit,
}: {
  person: Person;
  registry: RegistryDocument;
  teamsById: Map<string, string>;
  maps: Parameters<typeof matchesFilters>[2] | null;
  filters: Parameters<typeof matchesFilters>[1];
  /** Opens the project card (row click / title pencil → the shared dialog). */
  onEdit: (projectId: string) => void;
}): ReactElement {
  const setPersonPositions = useSetPersonPositions();
  const [sort, setSort] = useState<QueueSortKey>(DEFAULT_QUEUE_SORT);
  /** Per-card status filter — local view state only (a per-entity view
   * control, the same shape as the per-stream sort selects on «Потоки»;
   * deliberate STYLEGUIDE §6 deviation: one URL key per person id would be
   * unusable — the GLOBAL status filter stays URL-synced and composes). */
  const [statusFilter, setStatusFilter] = useState<string>('');
  // «Все статусы» + the managed dictionary in its order (owner request
  // 2026-10-14): renamed/custom statuses appear here automatically.
  const statusFilterOptions = useMemo(
    () => [{ value: '', label: 'Все статусы' }, ...statusOptions(registry.statuses)],
    [registry.statuses],
  );

  const visible = useMemo(() => {
    if (!maps) return [];
    return registry.projects.filter((project) => matchesFilters(project, filters, maps));
  }, [registry.projects, filters, maps]);

  /**
   * The per-card select narrows ONLY the table rows, never the report numbers:
   * `capacity` below keeps counting `visible` (the full global-filtered set),
   * so «Загрузка», the status counters and «Всего проектов» stay the person's
   * real report no matter which status is picked here (owner request
   * 2026-10-14). Composition: global filters apply first (F-13/F-14), then this
   * select narrows what is left.
   */
  const narrowed = useMemo(
    () => (statusFilter === '' ? visible : visible.filter((project) => project.status === statusFilter)),
    [visible, statusFilter],
  );

  const queue = useMemo(
    () => sortPersonQueueWith(person.id, narrowed, sort),
    [narrowed, person.id, sort],
  );

  // Deliberately the UN-narrowed set — see the `narrowed` comment above.
  const capacity: StatusReport = useMemo(() => personCapacity(person.id, visible), [visible, person.id]);

  /** One reorder = optimistic cache update + POST with the FULL new order. */
  const applyOrder = (nextIds: readonly string[] | null) => {
    if (!nextIds || nextIds.length === 0) return;
    setPersonPositions.mutate({ version: registry.version, personId: person.id, orderedProjectIds: [...nextIds] });
  };

  /**
   * The drag scope is the person's WHOLE queue, computed from the UNFILTERED
   * registry: the wire op (`POST /registry/people-positions`) demands the FULL
   * order, and posting only the visible subset is `400 invalid` (prod bug
   * 2026-10-26). `queue` above stays the display-only list — global filters
   * and the per-card status select narrow the rows shown, never the posted
   * list (same model as StreamSection and F-28 on «Все проекты»). A drop
   * therefore puts the dragged id at the target's slot in the FULL queue —
   * filters do not shift positions — and a keyboard nudge whose neighbour row
   * is filtered out looks like a no-op (the already-shipped convention on
   * «Потоки» / «Все проекты»).
   */
  const fullOrder = useMemo(
    () => orderedIds(sortPersonQueueManual(person.id, registry.projects)),
    [registry.projects, person.id],
  );

  // An inactive person keeps their section but has nothing to drag. Filters do
  // NOT gate the drag: the posted list is the full queue above, so no filter
  // can silently drop a hidden project from it.
  const controls = useManualOrder({
    enabled: sort === 'manual' && person.active,
    busy: setPersonPositions.isPending,
    order: fullOrder,
    onApply: applyOrder,
  });

  const rowOf = (project: Project) => ({
    project,
    assignment: project.assignments.find((a) => a.personId === person.id) ?? project.assignments[0],
  });

  /**
   * Row click = open the project card (owner request, «Исполнители»). Clicks on
   * an interactive control (select/input/button/link) belong to that control —
   * the same guard the row drag uses. Left button only.
   */
  const openFromRow = (project: Project) => (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement | null)?.closest(INTERACTIVE)) return;
    onEdit(project.id);
  };

  return (
    <section
      className={`card card--flush person-card${controls.enabled ? ' person-card--manual' : ''}`}
      aria-label={`Отчёт по сотруднику: ${person.name}`}
      data-testid={`person-${person.id}`}
    >
      <header className="person-card__header">
        <div className="person-card__who">
          <h2 className="person-card__name">{person.name}</h2>
          <p className="page-header__sub">{[person.role, person.email].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="person-card__meta">
          <Badge tone="brand" title="Команда сотрудника">
            {teamsById.get(person.teamId) ?? person.teamId}
          </Badge>
          {person.active ? null : <Badge tone="warn">неактивен</Badge>}
        </div>
        <div className="stream-section__tools">
          <label className="toolbar__label" htmlFor={`person-${person.id}-status-filter`}>
            Фильтр статуса
          </label>
          <select
            id={`person-${person.id}-status-filter`}
            className="input input--compact"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {statusFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="toolbar__label" htmlFor={`person-${person.id}-sort`}>
            Сортировка
          </label>
          <select
            id={`person-${person.id}-sort`}
            className="input input--compact"
            value={sort}
            disabled={!person.active}
            title={person.active ? undefined : 'Сотрудник неактивен — очередь не редактируется'}
            onChange={(event) => setSort(event.target.value as QueueSortKey)}
          >
            {QUEUE_SORT_KEYS.map((key) => (
              <option key={key} value={key}>
                {QUEUE_SORT_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
        <dl className="capacity">
          <div className="capacity__item capacity__item--total">
            <dt>Загрузка</dt>
            <dd>
              <span className="capacity__num">{capacity.hours}</span> ч
            </dd>
          </div>
          <div className="capacity__item">
            <dt>Согласовано</dt>
            <dd>
              <span className="capacity__num">{capacity.hoursByStatus.agreed}</span> ч · {capacity.byStatus.agreed}
            </dd>
          </div>
          <div className="capacity__item">
            <dt>В работе</dt>
            <dd>
              <span className="capacity__num">{capacity.hoursByStatus.in_work}</span> ч · {capacity.byStatus.in_work}
            </dd>
          </div>
          <div className="capacity__item">
            <dt>На поддержке</dt>
            <dd>
              <span className="capacity__num">{capacity.hoursByStatus.on_support}</span> ч · {capacity.byStatus.on_support}
            </dd>
          </div>
          <div className="capacity__item">
            <dt>Просрочено</dt>
            <dd className={capacity.overdue > 0 ? 'capacity__overdue' : undefined}>
              <span className="capacity__num">{capacity.overdue}</span>
            </dd>
          </div>
          <div className="capacity__item">
            <dt>Всего проектов</dt>
            <dd>
              <span className="capacity__num">{capacity.total}</span>
            </dd>
          </div>
        </dl>
      </header>

      {queue.length === 0 ? (
        visible.length > 0 ? (
          /* STYLEGUIDE §6: the filtered empty state names WHICH filter emptied
             the table — here the per-card select, not the global bar — and
             offers its reset. The counters above keep showing the full set. */
          <EmptyState
            title="Фильтр статуса скрыл все проекты"
            hint="В личной очереди нет проектов с выбранным статусом."
            action={
              <Button size="sm" variant="secondary" onClick={() => setStatusFilter('')}>
                Сбросить фильтр статуса
              </Button>
            }
          />
        ) : (
          <p className="person-card__empty">Проектов пока нет.</p>
        )
      ) : (
        <div className="table-wrap">
          <table className={controls.enabled ? 'data queue-table--manual' : 'data'}>
            <caption className="sr-only">Личная очередь {person.name}</caption>
            <thead>
              <tr>
                {controls.enabled ? <th scope="col" className="project-table__drag" aria-label="Ручной порядок" /> : null}
                <th scope="col">Проект</th>
                <th scope="col">Статус</th>
                <th scope="col">Приоритет</th>
                <th scope="col">Срок до</th>
                <th scope="col" className="num">
                  Часы
                </th>
              </tr>
            </thead>
            <tbody>
              {queue.map((project) => {
                const { assignment } = rowOf(project);
                const drag = manualRowDragProps(project.id, controls, {
                  title: project.title,
                  statusLabel: statusLabel(registry.statuses, project.status),
                  priorityLabel: PROJECT_PRIORITY_LABELS[project.priority],
                });
                const isDragging = controls.dragId === project.id;
                const isDropTarget = controls.overId === project.id && controls.dragId !== project.id;
                return (
                  <tr
                    key={project.id}
                    className={[
                      'queue-row--clickable',
                      priorityRowClass(project),
                      isDragging ? 'project-row--dragging' : '',
                      isDropTarget ? 'project-row--drop-target' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    title="Открыть карточку проекта"
                    draggable={drag.draggable}
                    onDragStart={drag.onDragStart}
                    onDragOver={drag.onDragOver}
                    onDrop={drag.onDrop}
                    onDragEnd={drag.onDragEnd}
                    onClick={openFromRow(project)}
                  >
                    {controls.enabled ? (
                      <DragHandleCell id={project.id} label={project.title} controls={controls} busy={setPersonPositions.isPending} />
                    ) : null}
                    <td className="cell-primary">
                      <span className="cell-primary__row">
                        <span className="cell-primary__text" title={project.title}>
                          {project.title}
                        </span>
                        {/* The keyboard path to the same dialog (the row itself
                            is not focusable): a real button, STYLEGUIDE §7. */}
                        <button
                          type="button"
                          className="icon-btn icon-btn--sm cell-primary__edit"
                          aria-label={`Редактировать: ${project.title}`}
                          title="Открыть карточку проекта"
                          onClick={() => onEdit(project.id)}
                        >
                          <Pencil size={14} aria-hidden="true" />
                        </button>
                      </span>
                    </td>
                    <td>
                      <Badge>{statusLabel(registry.statuses, project.status)}</Badge>
                    </td>
                    <td>
                      <span title="Приоритет в личной очереди">
                        {assignment ? PROJECT_PRIORITY_LABELS[assignment.personPriority] : '—'}
                      </span>
                    </td>
                    <td>
                      <span className="date-cell">
                        {formatDisplayDate(project.dueDate) || '—'}
                        {isOverdue(project) ? <Badge tone="bad">просрочено</Badge> : null}
                      </span>
                    </td>
                    <td className="num">{project.hours ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
